import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Download, Zap, Users, DollarSign, Target, CalendarCheck, TrendingUp, Layout } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => "$" + (n || 0).toLocaleString();

function Metric({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-white/10 p-5" style={{ background: "rgba(14,16,23,0.7)" }}>
      <div className="flex items-start justify-between">
        <div><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
          <p className="text-2xl sm:text-3xl font-display font-bold mt-2 text-white">{value}</p></div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}22` }}><Icon className="w-5 h-5" style={{ color: accent }} /></div>
      </div>
    </div>
  );
}

export default function PublicReport() {
  const { token } = useParams();
  const [p, setP] = useState(null);

  useEffect(() => { axios.get(`${API}/public/report/${token}`).then(({ data }) => setP(data)).catch(() => setP(false)); }, [token]);

  if (p === null) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#07080D" }}><Loader2 className="w-8 h-8 animate-spin text-indigo-400" /></div>;
  if (p === false) return <div className="min-h-screen flex items-center justify-center text-slate-400" style={{ background: "#07080D" }}>This report link is invalid or has expired.</div>;

  const d = p.data;
  return (
    <div className="min-h-screen text-slate-100 px-5 sm:px-8 py-10" style={{ background: "#07080D" }} data-testid="public-report">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center gap-3 justify-between mb-8">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)" }}><Zap className="w-4 h-4 text-white" /></div>
            <div><p className="font-display font-bold">{p.title}</p><p className="text-xs text-slate-500">Performance report · {d.range.from} → {d.range.to}</p></div>
          </div>
          <a href={`${API}/public/report/${token}/export`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white" style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)" }} data-testid="public-report-export"><Download className="w-4 h-4" />Download CSV</a>
        </div>

        {p.scope === "agency" ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Metric icon={Users} label="New Leads" value={d.totals.leads} accent="#6366F1" />
              <Metric icon={DollarSign} label="Revenue" value={money(d.totals.revenue)} accent="#10B981" />
              <Metric icon={CalendarCheck} label="Appointments" value={d.totals.appointments} accent="#06B6D4" />
              <Metric icon={TrendingUp} label="Show-up Rate" value={`${d.totals.show_rate}%`} accent="#F59E0B" />
            </div>
            <div className="rounded-2xl border border-white/10 overflow-x-auto" style={{ background: "rgba(14,16,23,0.7)" }}>
              <table className="w-full text-sm">
                <thead className="text-slate-500 border-b border-white/10"><tr className="[&>th]:text-left [&>th]:px-4 [&>th]:py-3 [&>th]:text-xs [&>th]:uppercase [&>th]:tracking-wider">
                  <th>Sub-account</th><th>Leads</th><th>Revenue</th><th>Won</th><th>Appts</th><th>Show %</th></tr></thead>
                <tbody className="divide-y divide-white/6">
                  {d.sub_accounts.map((s) => (<tr key={s.id} className="[&>td]:px-4 [&>td]:py-3">
                    <td className="font-medium">{s.name}</td><td>{s.metrics.leads}</td><td>{money(s.metrics.deals.revenue)}</td>
                    <td>{s.metrics.deals.won}</td><td>{s.metrics.appointments.total}</td><td>{s.metrics.appointments.show_rate}%</td></tr>))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Metric icon={Users} label="New Leads" value={d.metrics.leads} accent="#6366F1" />
              <Metric icon={DollarSign} label="Revenue" value={money(d.metrics.deals.revenue)} accent="#10B981" />
              <Metric icon={Target} label="Open Pipeline" value={money(d.metrics.deals.pipeline_value)} accent="#8B5CF6" />
              <Metric icon={TrendingUp} label="Win Rate" value={`${d.metrics.deals.win_rate}%`} accent="#F59E0B" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-white/10 p-5" style={{ background: "rgba(14,16,23,0.7)" }}>
                <div className="flex items-center gap-2 mb-4"><CalendarCheck className="w-4 h-4 text-indigo-400" /><h3 className="font-display font-semibold">Appointments</h3></div>
                {[["Completed", d.metrics.appointments.completed], ["No-shows", d.metrics.appointments.no_show], ["Show-up rate", `${d.metrics.appointments.show_rate}%`]].map(([l, v]) => (
                  <div key={l} className="flex justify-between text-sm py-1.5"><span className="text-slate-400">{l}</span><span className="font-semibold">{v}</span></div>))}
              </div>
              <div className="rounded-2xl border border-white/10 p-5" style={{ background: "rgba(14,16,23,0.7)" }}>
                <div className="flex items-center gap-2 mb-4"><Layout className="w-4 h-4 text-indigo-400" /><h3 className="font-display font-semibold">Funnels</h3></div>
                {d.funnels.length === 0 ? <p className="text-sm text-slate-500">No funnels.</p> : d.funnels.map((f, i) => (
                  <div key={i} className="flex justify-between text-sm py-1.5"><span className="text-slate-400 truncate">{f.name}</span><span className="font-semibold">{f.opt_in_rate}%</span></div>))}
              </div>
            </div>
          </>
        )}
        <p className="text-center text-xs text-slate-600 mt-10">Powered by Momento · The AI growth OS for agencies</p>
      </div>
    </div>
  );
}
